import { useEffect } from 'react';

const observedContainers = new WeakSet<Element>();

export function observeLegacyMacroLinks(
	container: Element,
	shimUrl: (url: string) => string,
): () => void {
	observedContainers.add(container);
	const rewrite = (anchor: HTMLAnchorElement) => {
		for (
			let parent = anchor.parentElement;
			parent && parent !== container;
			parent = parent.parentElement
		) {
			if (observedContainers.has(parent)) return;
		}
		const href = anchor.getAttribute('href');
		if (
			!container.contains(anchor) ||
			!href ||
			href.startsWith('#') ||
			anchor.target === '_self' ||
			anchor.closest('.ProseMirror, [contenteditable="true"], [data-smart-link-container]')
		) {
			return;
		}
		const converted = shimUrl(anchor.href);
		if (converted !== anchor.href) {
			anchor.setAttribute('href', converted);
		}
	};
	const scan = (node: Element) => {
		if (node instanceof HTMLAnchorElement) {
			rewrite(node);
		}
		node.querySelectorAll<HTMLAnchorElement>('a[href]').forEach(rewrite);
	};
	const observer = new MutationObserver((records) => {
		const added = new Set<Element>();
		const changed = new Set<HTMLAnchorElement>();
		for (const record of records) {
			if (record.type === 'attributes' && record.target instanceof HTMLAnchorElement) {
				changed.add(record.target);
			} else {
				record.addedNodes.forEach((node) => {
					if (node instanceof Element && container.contains(node)) added.add(node);
				});
			}
		}
		for (const node of added) {
			let parent = node.parentElement;
			while (parent && parent !== container && !added.has(parent)) parent = parent.parentElement;
			if (!parent || !added.has(parent)) scan(node);
		}
		changed.forEach(rewrite);
	});
	observer.observe(container, {
		childList: true,
		subtree: true,
		attributes: true,
		attributeFilter: ['href'],
	});
	scan(container);
	return () => {
		observer.disconnect();
		observedContainers.delete(container);
	};
}

export function useShimLegacyMacroLinks({
	container,
	shimUrl,
	enabled,
}: {
	container: Element | null;
	shimUrl: (url: string) => string;
	enabled: boolean;
}): void {
	useEffect(() => {
		if (!container || !enabled) return;
		return observeLegacyMacroLinks(container, shimUrl);
	}, [container, shimUrl, enabled]);
}
